/* eslint-disable max-lines -- one endpoint table for the core API domain; declarations only */
import type {
  ApiKeyCreateApiData,
  ApiKeyCreateApiResponses,
  ApiKeyDeleteApiData,
  ApiKeyDeleteApiResponses,
  ApiKeyGetApiData,
  ApiKeyGetApiResponses,
  ApiKeyListApiData,
  ApiKeyListApiResponses,
  ApiKeyOauthClientCreateApiData,
  ApiKeyOauthClientCreateApiResponses,
  ApiKeyOauthClientGetApiData,
  ApiKeyOauthClientGetApiResponses,
  ApiKeyOauthClientRotateSecretApiData,
  ApiKeyOauthClientRotateSecretApiResponses,
  ApiKeyPolicyAddApiData,
  ApiKeyPolicyAddApiResponses,
  ApiKeyPolicyRemoveApiData,
  ApiKeyPolicyRemoveApiResponses,
  ApiKeyRevokeApiData,
  ApiKeyRevokeApiResponses,
  ApiKeyUpdateApiData,
  ApiKeyUpdateApiResponses,
  AuditLogContentTypesListApiData,
  AuditLogContentTypesListApiResponses,
  AuditLogExportExcelApiData,
  AuditLogExportExcelApiResponses,
  AuditLogListApiData,
  AuditLogListApiResponses,
  AutoTranslateApiData,
  AutoTranslateApiResponses,
  CallTaskApiData,
  CallTaskApiResponses,
  DevSettingsGetApiData,
  DevSettingsGetApiResponses,
  FeatureFlagEvaluateApiData,
  FeatureFlagEvaluateApiResponses,
  FeatureFlagListApiData,
  FeatureFlagListApiResponses,
  FeatureFlagSetApiData,
  FeatureFlagSetApiResponses,
  LoginAttemptAccountListApiData,
  LoginAttemptAccountListApiResponses,
  LoginAttemptListApiData,
  LoginAttemptListApiResponses,
  LoginBlocklistEntryCreateApiData,
  LoginBlocklistEntryCreateApiResponses,
  LoginBlocklistEntryDeleteApiData,
  LoginBlocklistEntryDeleteApiResponses,
  LoginBlocklistEntryGetApiData,
  LoginBlocklistEntryGetApiResponses,
  LoginBlocklistEntryListApiData,
  LoginBlocklistEntryListApiResponses,
  LoginBlocklistEntryPatchApiData,
  LoginBlocklistEntryPatchApiResponses,
  MetabaseIframeUrlApiData,
  MetabaseIframeUrlApiResponses,
  OutboundWebhookAttemptGetApiData,
  OutboundWebhookAttemptGetApiResponses,
  OutboundWebhookEventGetApiData,
  OutboundWebhookEventGetApiResponses,
  OutboundWebhookEventListApiData,
  OutboundWebhookEventListApiResponses,
  OutboundWebhookEventResendApiData,
  OutboundWebhookEventResendApiResponses,
  PublicHolidaysListApiData,
  PublicHolidaysListApiResponses,
  ServiceDepartmentWebhookEndpointCreateApiData,
  ServiceDepartmentWebhookEndpointCreateApiResponses,
  ServiceDepartmentWebhookEndpointDeleteApiData,
  ServiceDepartmentWebhookEndpointDeleteApiResponses,
  ServiceDepartmentWebhookEndpointGetApiData,
  ServiceDepartmentWebhookEndpointGetApiResponses,
  ServiceDepartmentWebhookEndpointListApiData,
  ServiceDepartmentWebhookEndpointListApiResponses,
  ServiceDepartmentWebhookEndpointPatchApiData,
  ServiceDepartmentWebhookEndpointPatchApiResponses,
  ServiceOrganizationWebhookEndpointCreateApiData,
  ServiceOrganizationWebhookEndpointCreateApiResponses,
  ServiceOrganizationWebhookEndpointDeleteApiData,
  ServiceOrganizationWebhookEndpointDeleteApiResponses,
  ServiceOrganizationWebhookEndpointGetApiData,
  ServiceOrganizationWebhookEndpointGetApiResponses,
  ServiceOrganizationWebhookEndpointListApiData,
  ServiceOrganizationWebhookEndpointListApiResponses,
  ServiceOrganizationWebhookEndpointPatchApiData,
  ServiceOrganizationWebhookEndpointPatchApiResponses,
  TemporaryStateListApiData,
  TemporaryStateListApiResponses,
  TranslateTextApiData,
  TranslateTextApiResponses,
  VerificationStatusApiData,
  VerificationStatusApiResponses
} from '@navio/nsdk';
import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { backgroundJobUrls } from './backgroundJobUrls';

export {
  BACKGROUND_JOB_ACTIVE_STATUSES,
  BackgroundJobName,
  BackgroundJobRunStatus,
  BackgroundJobRunStatusLabels,
  BackgroundJobScheduleStatus,
  BackgroundJobScheduleStatusLabels,
  isAdapterRelatedBackgroundJobName,
  isBackgroundJobRunRevocable
} from './backgroundJobConstants';
// Value exports above also surface matching type aliases (BackgroundJobRunStatus, etc.).
export type {
  BackgroundJobRunRecord,
  BackgroundJobRunResultItemRecord,
  BackgroundJobRunResultRecord,
  BackgroundJobScheduleRecord
} from './backgroundJobConstants';

export const getCoreUrls = {
  ...backgroundJobUrls,
  getPaginatedApiKeys: dataEndpoint<ApiKeyListApiData, ApiKeyListApiResponses>({
    getUrl: (queryParams) => apiUrl('api-keys/', queryParams)
  }),
  getApiKey: dataEndpoint<ApiKeyGetApiData, ApiKeyGetApiResponses>({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/`)
  }),
  postApiKey: responseEndpoint<ApiKeyCreateApiData, ApiKeyCreateApiResponses>({
    getUrl: () => apiUrl('api-keys/'),
    getBody: (data) => data
  }),
  patchApiKey: responseEndpoint<ApiKeyUpdateApiData, ApiKeyUpdateApiResponses>({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/`),
    getBody: ({ apiKeyId: _, ...body }) => body
  }),
  deleteApiKey: dataEndpoint<ApiKeyDeleteApiData, ApiKeyDeleteApiResponses>({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/`)
  }),
  patchRevokeApiKey: responseEndpoint<ApiKeyRevokeApiData, ApiKeyRevokeApiResponses>({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/revoke/`)
  }),
  postApiKeyPolicy: responseEndpoint<ApiKeyPolicyAddApiData, ApiKeyPolicyAddApiResponses>({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/policies/`),
    getBody: ({ apiKeyId: _, ...data }) => data
  }),
  deleteApiKeyPolicy: responseEndpoint<ApiKeyPolicyRemoveApiData, ApiKeyPolicyRemoveApiResponses>({
    getUrl: ({ apiKeyId, policyId }) => apiUrl(`api-keys/${apiKeyId}/policies/${policyId}/`)
  }),
  getApiKeyOauthClient: dataEndpoint<ApiKeyOauthClientGetApiData, ApiKeyOauthClientGetApiResponses>({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/oauth-client/`)
  }),
  postApiKeyOauthClient: responseEndpoint<ApiKeyOauthClientCreateApiData, ApiKeyOauthClientCreateApiResponses>({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/oauth-client/`),
    getBody: () => ({})
  }),
  postApiKeyOauthClientRotateSecret: responseEndpoint<
    ApiKeyOauthClientRotateSecretApiData,
    ApiKeyOauthClientRotateSecretApiResponses
  >({
    getUrl: ({ apiKeyId }) => apiUrl(`api-keys/${apiKeyId}/oauth-client/rotate-secret/`),
    getBody: () => ({})
  }),
  getPaginatedAuditLogs: dataEndpoint<AuditLogListApiData, AuditLogListApiResponses>({
    getUrl: (queryParams) => apiUrl('audit-logs/', queryParams)
  }),
  getAuditLogContentTypes: dataEndpoint<AuditLogContentTypesListApiData, AuditLogContentTypesListApiResponses>({
    getUrl: (queryParams) => apiUrl('audit-logs/content-types/', queryParams)
  }),
  getAuditLogsExportExcel: dataEndpoint<AuditLogExportExcelApiData, AuditLogExportExcelApiResponses>({
    getUrl: (queryParams) => apiUrl('audit-logs/export-excel/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getMetabaseDashboard: dataEndpoint<MetabaseIframeUrlApiData, MetabaseIframeUrlApiResponses>({
    getUrl: (queryParams) => apiUrl('core/metabase/iframe-url/', queryParams)
  }),
  getVerificationStatus: dataEndpoint<VerificationStatusApiData, VerificationStatusApiResponses>({
    getUrl: (queryParams) => apiUrl('core/verification-status/', queryParams)
  }),
  getPublicHolidays: dataEndpoint<PublicHolidaysListApiData, PublicHolidaysListApiResponses>({
    getUrl: (queryParams) => apiUrl('core/public-holidays/', queryParams)
  }),
  postTranslateText: responseEndpoint<TranslateTextApiData, TranslateTextApiResponses>({
    getUrl: () => apiUrl('core/translate-text/'),
    getBody: (data) => data
  }),
  postAutoTranslateLocalizedContent: responseEndpoint<AutoTranslateApiData, AutoTranslateApiResponses>({
    getUrl: () => apiUrl('core/localization/auto-translate/'),
    getBody: (data) => data
  }),
  getPaginatedWebhookEvents: dataEndpoint<OutboundWebhookEventListApiData, OutboundWebhookEventListApiResponses>({
    getUrl: ({ webhookEndpointId, ...queryParams }) =>
      apiUrl(`webhook-endpoints/${webhookEndpointId}/events/`, queryParams)
  }),
  getPaginatedWebhooksForServiceDepartment: dataEndpoint<
    ServiceDepartmentWebhookEndpointListApiData,
    ServiceDepartmentWebhookEndpointListApiResponses
  >({
    getUrl: ({ serviceDepartmentId, ...queryParams }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/webhooks/`, queryParams)
  }),
  getPaginatedWebhooksForServiceOrganization: dataEndpoint<
    ServiceOrganizationWebhookEndpointListApiData,
    ServiceOrganizationWebhookEndpointListApiResponses
  >({
    getUrl: ({ serviceOrganizationId, ...queryParams }) =>
      apiUrl(`service-organizations/${serviceOrganizationId}/webhooks/`, queryParams)
  }),
  getServiceOrganizationWebhook: dataEndpoint<
    ServiceOrganizationWebhookEndpointGetApiData,
    ServiceOrganizationWebhookEndpointGetApiResponses
  >({
    getUrl: ({ serviceOrganizationId, webhookEndpointId }) =>
      apiUrl(`service-organizations/${serviceOrganizationId}/webhooks/${webhookEndpointId}/`)
  }),
  getWebhook: dataEndpoint<ServiceDepartmentWebhookEndpointGetApiData, ServiceDepartmentWebhookEndpointGetApiResponses>(
    {
      getUrl: ({ serviceDepartmentId, webhookEndpointId }) =>
        apiUrl(`service-departments/${serviceDepartmentId}/webhooks/${webhookEndpointId}/`)
    }
  ),
  postWebhook: responseEndpoint<
    ServiceDepartmentWebhookEndpointCreateApiData,
    ServiceDepartmentWebhookEndpointCreateApiResponses
  >({
    getUrl: ({ serviceDepartmentId }) => apiUrl(`service-departments/${serviceDepartmentId}/webhooks/`),
    getBody: ({ serviceDepartmentId: _, ...data }) => data
  }),
  postServiceOrganizationWebhook: responseEndpoint<
    ServiceOrganizationWebhookEndpointCreateApiData,
    ServiceOrganizationWebhookEndpointCreateApiResponses
  >({
    getUrl: ({ serviceOrganizationId }) => apiUrl(`service-organizations/${serviceOrganizationId}/webhooks/`),
    getBody: ({ serviceOrganizationId: _, ...data }) => data
  }),
  patchWebhook: responseEndpoint<
    ServiceDepartmentWebhookEndpointPatchApiData,
    ServiceDepartmentWebhookEndpointPatchApiResponses
  >({
    getUrl: ({ serviceDepartmentId, webhookEndpointId }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/webhooks/${webhookEndpointId}/`),
    getBody: ({ serviceDepartmentId: _, webhookEndpointId: __, ...data }) => data
  }),
  patchServiceOrganizationWebhook: responseEndpoint<
    ServiceOrganizationWebhookEndpointPatchApiData,
    ServiceOrganizationWebhookEndpointPatchApiResponses
  >({
    getUrl: ({ serviceOrganizationId, webhookEndpointId }) =>
      apiUrl(`service-organizations/${serviceOrganizationId}/webhooks/${webhookEndpointId}/`),
    getBody: ({ serviceOrganizationId: _, webhookEndpointId: __, ...data }) => data
  }),
  deleteWebhook: dataEndpoint<
    ServiceDepartmentWebhookEndpointDeleteApiData,
    ServiceDepartmentWebhookEndpointDeleteApiResponses
  >({
    getUrl: ({ serviceDepartmentId, webhookEndpointId }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/webhooks/${webhookEndpointId}/`)
  }),
  deleteServiceOrganizationWebhook: dataEndpoint<
    ServiceOrganizationWebhookEndpointDeleteApiData,
    ServiceOrganizationWebhookEndpointDeleteApiResponses
  >({
    getUrl: ({ serviceOrganizationId, webhookEndpointId }) =>
      apiUrl(`service-organizations/${serviceOrganizationId}/webhooks/${webhookEndpointId}/`)
  }),
  getWebhookEvent: dataEndpoint<OutboundWebhookEventGetApiData, OutboundWebhookEventGetApiResponses>({
    getUrl: ({ outboundWebhookEventId }) => apiUrl(`outbound-webhook-events/${outboundWebhookEventId}/`)
  }),
  getWebhookAttempt: dataEndpoint<OutboundWebhookAttemptGetApiData, OutboundWebhookAttemptGetApiResponses>({
    getUrl: ({ outboundWebhookAttemptId }) => apiUrl(`outbound-webhook-attempts/${outboundWebhookAttemptId}/`)
  }),
  postResendWebhookEvent: responseEndpoint<OutboundWebhookEventResendApiData, OutboundWebhookEventResendApiResponses>({
    getUrl: ({ outboundWebhookEventId }) => apiUrl(`outbound-webhook-events/${outboundWebhookEventId}/resend/`)
  }),
  postCallTask: responseEndpoint<CallTaskApiData, CallTaskApiResponses>({
    getUrl: () => apiUrl('core/call-task/'),
    getBody: (data) => data
  }),
  getDevSettings: dataEndpoint<DevSettingsGetApiData, DevSettingsGetApiResponses>({
    getUrl: () => apiUrl('core/dev-settings/')
  }),
  getFeatureFlags: dataEndpoint<FeatureFlagListApiData, FeatureFlagListApiResponses>({
    getUrl: () => apiUrl('core/feature-flags/')
  }),
  getFeatureFlagEvaluate: dataEndpoint<FeatureFlagEvaluateApiData, FeatureFlagEvaluateApiResponses>({
    getUrl: () => apiUrl('core/feature-flags/evaluate/')
  }),
  patchFeatureFlag: responseEndpoint<FeatureFlagSetApiData, FeatureFlagSetApiResponses>({
    getUrl: ({ flagKey }) => apiUrl(`core/feature-flags/${encodeURIComponent(flagKey)}/`),
    getBody: ({ flagKey: _, ...body }) => body
  }),
  getPaginatedLoginAttemptAccounts: dataEndpoint<LoginAttemptAccountListApiData, LoginAttemptAccountListApiResponses>({
    getUrl: (queryParams) => apiUrl('login-attempt-accounts/', queryParams)
  }),
  getPaginatedLoginAttempts: dataEndpoint<LoginAttemptListApiData, LoginAttemptListApiResponses>({
    getUrl: (queryParams) => apiUrl('login-attempts/', queryParams)
  }),
  getPaginatedLoginBlocklistEntries: dataEndpoint<LoginBlocklistEntryListApiData, LoginBlocklistEntryListApiResponses>({
    getUrl: (queryParams) => apiUrl('login-blocklist/', queryParams)
  }),
  getPaginatedTemporaryStates: dataEndpoint<TemporaryStateListApiData, TemporaryStateListApiResponses>({
    getUrl: (queryParams) => apiUrl('temporary-states/', queryParams)
  }),
  getLoginBlocklistEntry: dataEndpoint<LoginBlocklistEntryGetApiData, LoginBlocklistEntryGetApiResponses>({
    getUrl: ({ loginBlocklistEntryId }) => apiUrl(`login-blocklist/${loginBlocklistEntryId}/`)
  }),
  postLoginBlocklistEntry: responseEndpoint<LoginBlocklistEntryCreateApiData, LoginBlocklistEntryCreateApiResponses>({
    getUrl: () => apiUrl('login-blocklist/'),
    getBody: (data) => data
  }),
  patchLoginBlocklistEntry: responseEndpoint<LoginBlocklistEntryPatchApiData, LoginBlocklistEntryPatchApiResponses>({
    getUrl: ({ loginBlocklistEntryId }) => apiUrl(`login-blocklist/${loginBlocklistEntryId}/`),
    getBody: ({ loginBlocklistEntryId: _, ...body }) => body
  }),
  deleteLoginBlocklistEntry: dataEndpoint<LoginBlocklistEntryDeleteApiData, LoginBlocklistEntryDeleteApiResponses>({
    getUrl: ({ loginBlocklistEntryId }) => apiUrl(`login-blocklist/${loginBlocklistEntryId}/`)
  })
};
